import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditAdminUser {
  readonly id: string;
  readonly full_name: string;
  readonly email: string;
}

export interface AuditLogEntry {
  readonly id: string;
  readonly admin_user_id: string;
  readonly resource: string;
  readonly action: string;
  readonly resource_id: string | null;
  readonly before_data: Record<string, unknown> | null;
  readonly after_data: Record<string, unknown> | null;
  readonly metadata: Record<string, unknown> | null;
  readonly created_at: string;
  readonly admin_user: AuditAdminUser | null;
}

export interface AuditLogListResponse {
  readonly entries: readonly AuditLogEntry[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
  readonly filters?: {
    readonly resources: readonly string[];
    readonly actions: readonly string[];
  };
}

export interface AuditLogListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly admin_user_id?: string | null;
  readonly resource?: string | null;
  readonly action?: string | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
  readonly q?: string | null;
  readonly include_filters?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  );
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const auditLogApi = {
  /** Fetch paginated audit log entries with optional filters. */
  list: (params: AuditLogListParams = {}) => {
    const qs = toQueryString({
      page: params.page,
      limit: params.limit,
      admin_user_id: params.admin_user_id,
      resource: params.resource,
      action: params.action,
      date_from: params.date_from,
      date_to: params.date_to,
      q: params.q,
      include_filters: params.include_filters,
    });
    return adminFetch<AuditLogListResponse>(`/api/admin/audit-log${qs}`);
  },
} as const;
